/**
 * ContentPostFirestoreService — CRUD for the canonical AI-output Post collection at:
 *   projects/{pid}/content_posts/{id}
 *
 * Concurrent Minsky refactor (2026-04-29):
 *   Replaces the retired `content_strategies/{sid}/sprints/{sid}/posts/*` and
 *   `master_posts/{id}` paths. All applied master+derivative posts (both
 *   topic-scope and sprint-scope Apply) live in this single collection.
 *
 * Pattern matches AuthorContentCorpusService — Firestore-direct CRUD for
 * pure metadata; AI-generation pipelines remain on backend endpoints.
 */

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  type DocumentData,
  type QueryFieldFilterConstraint,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { Post, PostLifecycleStage, PostKind } from '../types';

class ContentPostFirestoreService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private collRef(projectId: string) {
    return collection(this.db, 'projects', projectId, 'content_posts');
  }

  async getById(projectId: string, postId: string): Promise<Post | null> {
    const snap = await getDoc(doc(this.collRef(projectId), postId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Post;
  }

  /** One-shot read by topicId (no realtime). */
  async listByTopic(projectId: string, topicId: string, authorId?: string | null): Promise<Post[]> {
    const snap = await getDocs(
      authorId
        ? query(this.collRef(projectId), where('authorId', '==', authorId), where('topicId', '==', topicId))
        : query(this.collRef(projectId), where('topicId', '==', topicId)),
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Post));
  }

  /** One-shot read by sprintId (no realtime). */
  async listBySprint(projectId: string, sprintId: string, authorId?: string | null): Promise<Post[]> {
    const snap = await getDocs(
      authorId
        ? query(this.collRef(projectId), where('authorId', '==', authorId), where('sprintId', '==', sprintId))
        : query(this.collRef(projectId), where('sprintId', '==', sprintId)),
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Post));
  }

  /** One-shot read by strategyId (no realtime). */
  async listByStrategy(projectId: string, strategyId: string, authorId?: string | null): Promise<Post[]> {
    const snap = await getDocs(
      authorId
        ? query(this.collRef(projectId), where('authorId', '==', authorId), where('strategyId', '==', strategyId))
        : query(this.collRef(projectId), where('strategyId', '==', strategyId)),
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Post));
  }

  /**
   * Realtime subscription with optional filters. Pass exactly ONE narrowing
   * filter — Firestore composite indexes need to align (see firestore.indexes.json).
   */
  subscribe(
    projectId: string,
    filter: { authorId?: string | null; topicId?: string; sprintId?: string; strategyId?: string },
    onSnap: (posts: Post[]) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe {
    let q = query(this.collRef(projectId));
    const clauses: QueryFieldFilterConstraint[] = [];
    if (filter.authorId && filter.authorId !== 'all') clauses.push(where('authorId', '==', filter.authorId));
    if (filter.topicId) clauses.push(where('topicId', '==', filter.topicId));
    else if (filter.sprintId) clauses.push(where('sprintId', '==', filter.sprintId));
    else if (filter.strategyId) clauses.push(where('strategyId', '==', filter.strategyId));
    if (clauses.length > 0) q = query(this.collRef(projectId), ...clauses);

    return onSnapshot(
      q,
      (snap: QuerySnapshot<DocumentData>) => {
        const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Post));
        onSnap(posts);
      },
      (err) => onError?.(err),
    );
  }

  /**
   * Update content/curation fields. Audit fields (whyForAudience, dataUsage,
   * dedupFingerprint) are NEVER edited from the UI — they're AI-output only.
   */
  async updateContent(
    projectId: string,
    postId: string,
    patch: Partial<Pick<Post,
      'hook' | 'body' | 'cta' | 'notes' | 'title' | 'aiAssets' |
      'designAssetBrief' | 'generationPlanContext' | 'assetPlanningContext' |
      'exampleForTopicIds' | 'exampleForPillarIds' | 'exampleForPostTypes'>>,
  ): Promise<void> {
    const ref = doc(this.collRef(projectId), postId);
    const payload: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
    for (const k of Object.keys(payload)) {
      if (payload[k] === undefined) delete payload[k];
    }
    await updateDoc(ref, payload);
  }

  /**
   * Update lifecycleStage — promotes a topic-applied post into a sprint
   * (used by SprintPlanner when pulling existing posts from backlog).
   */
  async assignToSprint(
    projectId: string,
    postId: string,
    opts: { strategyId: string; sprintId: string },
  ): Promise<void> {
    const ref = doc(this.collRef(projectId), postId);
    await updateDoc(ref, {
      lifecycleStage: 'applied-to-sprint' as PostLifecycleStage,
      strategyId: opts.strategyId,
      sprintId: opts.sprintId,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Inverse — release a sprint binding back to topic-only.
   */
  async unassignFromSprint(projectId: string, postId: string): Promise<void> {
    const ref = doc(this.collRef(projectId), postId);
    await updateDoc(ref, {
      lifecycleStage: 'applied-to-topic' as PostLifecycleStage,
      strategyId: null,
      sprintId: null,
      updatedAt: serverTimestamp(),
    });
  }

  async delete(projectId: string, postId: string): Promise<void> {
    await deleteDoc(doc(this.collRef(projectId), postId));
  }

  /**
   * Upsert helper — used by Apply flow on the frontend (sprint→content_posts
   * write happens server-side, but legacy backfill paths still touch this).
   */
  async upsert(projectId: string, post: Post): Promise<void> {
    await setDoc(doc(this.collRef(projectId), post.id), post as unknown as Record<string, unknown>);
  }

  /**
   * Convenience query: filter by lifecycleStage + kind via collection-level
   * query (composite index required: lifecycleStage ASC, kind ASC).
   */
  async listByStage(
    projectId: string,
    stage: PostLifecycleStage,
    kind?: PostKind,
  ): Promise<Post[]> {
    const filters = [where('lifecycleStage', '==', stage)];
    if (kind) filters.push(where('kind', '==', kind));
    const snap = await getDocs(query(this.collRef(projectId), ...filters));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Post));
  }
}

export const contentPostFirestoreService = new ContentPostFirestoreService();
export { ContentPostFirestoreService };

/**
 * Collection-group reader for AI-example candidates across both self-author
 * and tracked-creator scopes. Used by `useAuthorPostsForCuration` hook.
 *
 * Index required: collection-group `author_posts`, fields:
 *   projectId ASC, exampleForTopicIds ARRAY-CONTAINS, kind ASC
 */
export async function listAuthorPostsByExampleTopic(
  projectId: string,
  topicId: string,
): Promise<Array<{
  id: string;
  authorBasePath?: 'authors' | 'external_authors';
  authorId: string;
  platformId?: string;
  data: Record<string, unknown>;
}>> {
  const db = CoreFirebaseService.getInstance().firestore;
  const snap = await getDocs(
    query(
      collectionGroup(db, 'author_posts'),
      where('projectId', '==', projectId),
      where('exampleForTopicIds', 'array-contains', topicId),
    ),
  );
  return snap.docs.map((d) => {
    // path: projects/{pid}/{authorBasePath}/{aid}/platforms/{pid}/author_posts/{id}
    const segments = d.ref.path.split('/');
    const authorBasePath = segments[2] as 'authors' | 'external_authors' | undefined;
    const authorId = segments[3];
    const platformId = segments[5];
    return {
      id: d.id,
      authorBasePath,
      authorId,
      platformId,
      data: d.data() as Record<string, unknown>,
    };
  });
}
